import React, { useEffect, useRef, useState } from "react";

import moment from "moment";

import { TIME_FORMAT } from "../../constants/dateFormat";
import MaskedTextFieldBase from "../form-fields/MaskedTextField";

import type { JSX } from "react";

interface InputDurationProps {
  disabled?: boolean;
  onChange: (value: string) => void;
  value: number;
  placeholder?: string;
}

function formatDuration(value: number): string {
  if (!Number.isFinite(value)) return "";
  return moment().startOf("day").seconds(value).format(TIME_FORMAT);
}

function parseDuration(text: string): number {
  return moment(text, TIME_FORMAT).diff(moment().startOf("day"), "seconds");
}

function InputDuration({
  disabled,
  onChange,
  value,
  placeholder,
}: InputDurationProps): JSX.Element {
  const [duration, setDuration] = useState(formatDuration(value));

  // Latest typed text, kept in a ref so the sync effect can read it without
  // depending on it — otherwise it would re-run on every keystroke and fight
  // the user's input.
  const durationRef = useRef(duration);
  durationRef.current = duration;

  // Reformat from `value` only when it represents a duration the field isn't
  // already showing (e.g. an external Range slider) — never as an echo of the
  // user's own typing, since the parent re-derives `value` on every keystroke.
  useEffect(() => {
    if (parseDuration(durationRef.current) !== value) {
      setDuration(formatDuration(value));
    }
  }, [value]);

  const handleChange = (next: string): void => {
    setDuration(next);
    onChange(next);
  };

  return (
    <MaskedTextFieldBase
      disabled={disabled}
      maskOptions={{ mask: "00:00:00" }}
      onChange={handleChange}
      placeholder={placeholder}
      value={duration}
    />
  );
}

export default InputDuration;
